import * as React from 'react';
import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';

import useCurrentUser from 'hooks/useCurrentUser';
import useResponsive from 'hooks/useResponsive';

import { drawerWidth, drawerWidthMD, drawerWidthCollapsed } from './Sidebar.constants';
import { SideBarProps } from './Sidebar.types';
import SidebarContent from './SidebarContent';
import useNavigationGroups from './hooks/useNavigationGroups';
import useSidebarActions from './hooks/useSidebarActions';

const noop = (): void => {};

const SideBar = ({
  variant = 'permanent',
  open = true,
  onClose,
  isCollapsed = false,
  onToggleCollapsed = noop,
}: SideBarProps) => {
  const { pathname, search } = useLocation();
  const currentUser = useCurrentUser();
  const { handleSignOut } = useSidebarActions();
  const { isMobile } = useResponsive();
  const groups = useNavigationGroups(currentUser, isMobile);

  const effectiveCollapsed = variant === 'temporary' ? false : isCollapsed;

  const content = (
    <SidebarContent
      groups={groups}
      isCollapsed={effectiveCollapsed}
      variant={variant}
      currentPath={pathname}
      currentSearch={search}
      onToggleCollapsed={onToggleCollapsed}
      onClose={onClose}
      onSignOut={handleSignOut}
    />
  );

  if (variant === 'temporary') {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: { xs: drawerWidthMD, xl: drawerWidth }, border: 0 },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        width: effectiveCollapsed ? `${drawerWidthCollapsed}px` : { md: drawerWidthMD, xl: drawerWidth },
        flexShrink: 0,
        transition: 'width 0s ease-in-out',
      }}
      aria-label="sidebar navigation"
    >
      <Drawer
        variant="permanent"
        sx={{
          display: 'block',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            position: 'relative',
            width: effectiveCollapsed ? `${drawerWidthCollapsed}px` : { md: drawerWidthMD, xl: drawerWidth },
            border: 0,
          },
        }}
        open
      >
        {content}
      </Drawer>
    </Box>
  );
};

export default SideBar;
